import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { AlertCircle, CheckCircle } from 'lucide-react';

import { AuthButton } from '../../components/auth/AuthButton';
import { AuthLayout } from '../../components/auth/AuthLayout';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { userReducer } from '../../store/reducers/user.reducer';
import { history } from '../../store/store';
import { SUPPORT_EMAIL, buildSupportMailto } from '../../utils/support';

const { verifyEmail } = userReducer.actions;

export const VerifyEmailPage = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();

  const verificationSuccess = useAppSelector((state) => state.user.verificationSuccess);
  const verificationError = useAppSelector((state) => state.user.verificationError);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get('token');
    if (token) {
      dispatch(verifyEmail({ token }));
    }
  }, [dispatch, location.search]);

  if (!verificationSuccess && !verificationError) {
    return (
      <AuthLayout
        title="Verifying Email"
        subtitle="Please wait while we verify your email address."
        cardClassName="md:max-h-[90dvh]"
      >
        <div className="flex items-center justify-center my-4">
          <div className="w-8 h-8 border-2 border-green01/30 border-t-green01 rounded-full animate-spin" />
        </div>
      </AuthLayout>
    );
  }

  if (verificationSuccess) {
    return (
      <AuthLayout title="Email Verified" subtitle="Your email has been successfully verified.">
        <div className="flex flex-col items-center gap-4 my-2">
          <CheckCircle size={48} className="text-green01" />
          <p className="text-greyP2 text-sm text-center">
            Thank you for verifying your email address. You can now log in to your account.
          </p>
          <AuthButton onClick={() => history.push('/login')}>Go to Login</AuthButton>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Verification Failed" subtitle="We couldn't verify your email address.">
      <div className="flex flex-col items-center gap-4 my-2">
        <AlertCircle size={48} className="text-[#FF8A00]" />
        <p className="text-greyP2 text-sm text-center">
          {verificationError || 'An error occurred during verification. Please try again or contact support.'}
        </p>
        <p className="text-greyP2 text-xs text-center">
          Need help? Write to{' '}
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            onClick={(e) => {
              e.currentTarget.href = buildSupportMailto();
            }}
            className="text-green01 hover:underline"
          >
            {SUPPORT_EMAIL}
          </a>
        </p>
        <AuthButton variant="outlined" onClick={() => history.push('/login')}>
          Back to Login
        </AuthButton>
      </div>
    </AuthLayout>
  );
};
